import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { advisorInfoSchema } from "../../yup";
import { useUpdateAdvisorMutation } from "../../services/api";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { updatePurlUser } from "../../store/reducers/purlUserReducer";
import { IoIosCloseCircle } from "react-icons/io";
import Loader from "../Loader";
import { tracker } from "../../services/mixpanel";

interface AdvisorFieldValues {
  advisorName: string;
  advisorEmail: string;
  advisorPhone: string;
}

interface AdvisorFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<AdvisorFieldValues>;
}

export const AdvisorForm = ({
  isOpen,
  onClose,
  initialValues,
}: AdvisorFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdvisorFieldValues>({
    resolver: yupResolver(advisorInfoSchema),
    defaultValues: initialValues,
  });
  const [updateAdvisor, { isLoading: advisorLoading }] =
    useUpdateAdvisorMutation();

  const loginData = useAppSelector((state) => state.login);
  const dispatch = useAppDispatch();

  const submitAdvisor: SubmitHandler<AdvisorFieldValues> = async (values) => {
    tracker.dashboard.advisor.formSubmit(!!initialValues?.advisorEmail);
    try {
      const sendData = {
        crm_Contact_ID: loginData.zohoCustomerId,
        advisorName: values.advisorName,
        advisorEmail: values.advisorEmail,
        advisorPhone: values.advisorPhone,
      };

      const result = await updateAdvisor(sendData).unwrap();
      if (result.data.code !== "success") throw new Error(result?.message);

      // Update Redux store with new advisor data
      dispatch(
        updatePurlUser({
          advisorName: values.advisorName,
          advisorEmail: values.advisorEmail,
          advisorPhone: values.advisorPhone,
        })
      );

      onClose();
    } catch (err) {
      console.log("err in updating advisor", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-full h-full fixed flex justify-center items-center bg-opacity-40 bg-black z-10">
      <form
        onSubmit={handleSubmit(submitAdvisor)}
        className="w-1/3 min-w-[300px] bg-[#CFD8E4] p-8 rounded-xl text-center relative"
      >
        <IoIosCloseCircle
          onClick={onClose}
          className="absolute right-2 top-2 text-2xl cursor-pointer"
        />
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            Advisor Name
          </label>
          <input
            {...register("advisorName")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.advisorName && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.advisorName.message}
            </p>
          )}
        </div>
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            Advisor Email
          </label>
          <input
            {...register("advisorEmail")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.advisorEmail && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.advisorEmail.message}
            </p>
          )}
        </div>
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            Advisor Phone Number
          </label>
          <input
            {...register("advisorPhone")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.advisorPhone && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.advisorPhone.message}
            </p>
          )}
        </div>
        <button className="h-[32px] px-6 rounded-sm bg-[#356C58] text-white">
          {advisorLoading ? <Loader isLoading={true} size={20} /> : "SUBMIT"}
        </button>
      </form>
    </div>
  );
};
